import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  FolderKanban, 
  CheckSquare, 
  TrendingUp, 
  Clock, 
  CheckCircle2,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  DollarSign,
  AlertCircle,
  Calendar,
  Briefcase,
  Target,
  Zap,
  MessageSquare,
  Video,
  Bot,
  Building2,
  HardHat,
  Wrench,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchRecentActivity();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await api.get('/dashboard/recent-activity');
      setRecentActivity(response.data || []);
    } catch (error) {
      console.error('Failed to load recent activity');
    }
  };

  // Enhanced stat cards with trend indicators
  const statCards = [
    {
      title: 'Active Projects',
      value: stats?.active_projects || 0,
      total: stats?.total_projects || 0,
      icon: FolderKanban,
      trend: '+12%',
      trendUp: true,
      gradient: 'from-[#C9A961]/20 to-[#C9A961]/5',
    },
    {
      title: 'Total Tasks',
      value: stats?.total_tasks || 0,
      completed: stats?.completed_tasks || 0,
      icon: CheckSquare,
      trend: '+8%',
      trendUp: true,
      gradient: 'from-[#C9A961]/20 to-[#C9A961]/5',
    },
    {
      title: 'Team Members',
      value: stats?.total_employees || 12,
      active: stats?.active_employees || 10,
      icon: Users,
      trend: '+2',
      trendUp: true,
      gradient: 'from-[#C9A961]/20 to-[#C9A961]/5',
    },
    {
      title: 'Total Clients',
      value: stats?.total_clients || 0,
      new: stats?.new_clients || 0,
      icon: Briefcase,
      trend: '+5',
      trendUp: true,
      gradient: 'from-[#C9A961]/20 to-[#C9A961]/5',
    },
  ];

  // Active projects with progress
  const activeProjects = [
    {
      name: 'Commercial Complex - Downtown',
      client: 'Acme Corporation',
      progress: 75,
      status: 'On Track',
      deadline: '2 weeks',
      priority: 'high',
    },
    {
      name: 'Residential Tower - Westside',
      client: 'TechStart Inc',
      progress: 60,
      status: 'In Progress',
      deadline: '1 month',
      priority: 'medium',
    },
    {
      name: 'Office Renovation - Suite 400',
      client: 'Global Ventures',
      progress: 40,
      status: 'Behind',
      deadline: '3 weeks',
      priority: 'critical',
    },
  ];

  // Upcoming deadlines
  const upcomingDeadlines = [
    { task: 'Foundation Inspection', project: 'Downtown Complex', due: 'Tomorrow', priority: 'critical' },
    { task: 'Material Delivery', project: 'Westside Tower', due: '3 days', priority: 'high' },
    { task: 'Client Review Meeting', project: 'Suite 400', due: '5 days', priority: 'medium' },
    { task: 'Permit Submission', project: 'East Gate', due: '1 week', priority: 'high' },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'high': return 'bg-[#C9A961]/30 text-[#C9A961] border-[#C9A961]/50';
      case 'medium': return 'bg-[#C9A961]/20 text-[#C9A961] border-[#C9A961]/50';
      case 'low': return 'bg-[#C9A961]/10 text-[#C9A961]/80 border-[#C9A961]/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Track': return 'text-[#C9A961]';
      case 'In Progress': return 'text-[#C9A961]/80';
      case 'Behind': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="space-y-4 text-center">
          <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gold text-lg">Loading your command center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8" data-testid="dashboard-page">
      {/* Hero Header - No Border, Bigger Logo */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-dark-card via-black to-dark-card p-10 shadow-gold">
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-4">
            <img src="/williams-logo-transparent.png" alt="Williams Diversified" className="h-24 md:h-28" />
            <div>
              <h1 className="text-5xl md:text-6xl font-bold font-serif">
                <span className="text-white">Command</span>{' '}
                <span className="text-[#C9A961] italic">Center</span>
              </h1>
              <p className="text-gray-400 text-xl mt-2">Williams Diversified LLC</p>
            </div>
          </div>
          <p className="text-gray-300 text-base max-w-2xl">
            Real-time project oversight and business intelligence for your construction operations
          </p>
          <div className="flex gap-3 mt-8">
            <Button 
              onClick={() => navigate('/projects')}
              className="bg-[#C9A961] text-black hover:bg-[#B8985A] hover:scale-105 transition-all duration-200 shadow-gold text-base px-6 py-6"
            >
              <Zap className="mr-2 h-5 w-5" />
              View All Projects
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/tasks')}
              className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-black hover:scale-105 transition-all duration-200 text-base px-6 py-6"
            >
              <Target className="mr-2 h-5 w-5" />
              Manage Tasks
            </Button>
          </div>
        </div>
      </div>

      {/* Enhanced Stat Cards - Gold Hover */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={index}
              className="bg-dark-card shadow-card hover:shadow-gold hover:-translate-y-2 hover:bg-gradient-to-br hover:from-[#C9A961]/20 hover:to-transparent transition-all duration-300 cursor-pointer group overflow-hidden relative border-0"
            >
              <CardContent className="p-8 relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} group-hover:scale-110 group-hover:bg-[#C9A961]/30 transition-all duration-300`}>
                    <Icon className="h-6 w-6 text-gold group-hover:text-[#C9A961]" />
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    {stat.trendUp ? (
                      <ArrowUpRight className="h-4 w-4 text-[#C9A961]" />
                    ) : (
                      <ArrowDownRight className="h-4 w-4 text-red-400" />
                    )}
                    <span className={stat.trendUp ? 'text-[#C9A961]' : 'text-red-400'}>
                      {stat.trend}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1 group-hover:text-[#C9A961] transition-colors">{stat.title}</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-white group-hover:text-[#C9A961] transition-colors">{stat.value}</p>
                    {stat.total && (
                      <span className="text-sm text-gray-500 group-hover:text-[#C9A961]/70">/ {stat.total}</span>
                    )}
                    {stat.completed !== undefined && (
                      <span className="text-sm text-[#C9A961]">({stat.completed} done)</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Projects - Takes 2 columns */}
        <Card className="lg:col-span-2 bg-dark-card border-dark-border shadow-card">
          <CardHeader className="border-b border-dark-border">
            <div className="flex items-center justify-between">
              <CardTitle className="text-gold flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Active Projects
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/projects')}
                className="text-gold hover:bg-gold/10"
              >
                View All
                <ArrowUpRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {activeProjects.map((project, index) => (
              <div 
                key={index}
                className="p-4 rounded-xl bg-black/40 border border-dark-border hover:border-gold/30 hover:bg-black/60 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-white group-hover:text-gold transition-colors">
                      {project.name}
                    </h4>
                    <p className="text-sm text-gray-400 mt-1">{project.client}</p>
                  </div>
                  <Badge className={`${getPriorityColor(project.priority)} border`}>
                    {project.priority}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className={getStatusColor(project.status)}>{project.status}</span>
                    <span className="text-gray-500">Due in {project.deadline}</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Progress</span>
                      <span className="text-gold font-medium">{project.progress}%</span>
                    </div>
                    <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-gold to-gold-light transition-all duration-500 progress-bar"
                        style={{ width: `${project.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        <Card className="bg-dark-card border-dark-border shadow-card">
          <CardHeader className="border-b border-dark-border">
            <CardTitle className="text-gold flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Urgent Tasks
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              {upcomingDeadlines.map((item, index) => (
                <div 
                  key={index}
                  className="p-3 rounded-lg bg-black/40 border border-dark-border hover:border-gold/30 hover:bg-black/60 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-1 h-12 rounded-full ${
                      item.priority === 'critical' ? 'bg-red-500' :
                      item.priority === 'high' ? 'bg-[#C9A961]' :
                      item.priority === 'medium' ? 'bg-[#C9A961]/70' : 'bg-[#C9A961]/50'
                    }`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{item.task}</p>
                      <p className="text-xs text-gray-500 mt-1">{item.project}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Clock className="h-3 w-3 text-gray-500" />
                        <span className="text-xs text-gray-400">{item.due}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operations Hub - Entry Point */}
      <Card 
        className="relative overflow-hidden bg-dark-card border-dark-border hover:border-[#C9A961]/50 cursor-pointer transition-all duration-300 group"
        onClick={() => navigate('/command-center')}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-2xl font-bold text-white group-hover:text-[#C9A961] transition-colors">Operations Hub</h2>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#22c55e' }} />
                  <span className="text-xs" style={{ color: '#4ade80' }}>Live</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(201, 169, 97, 0.15)', border: '1px solid rgba(201, 169, 97, 0.3)' }}>
                  <Building2 className="h-4 w-4" style={{ color: '#C9A961' }} />
                  <span className="text-sm font-semibold" style={{ color: '#C9A961' }}>GOV</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(201, 169, 97, 0.15)', border: '1px solid rgba(201, 169, 97, 0.3)' }}>
                  <HardHat className="h-4 w-4" style={{ color: '#C9A961' }} />
                  <span className="text-sm font-semibold" style={{ color: '#C9A961' }}>GC</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(201, 169, 97, 0.15)', border: '1px solid rgba(201, 169, 97, 0.3)' }}>
                  <Wrench className="h-4 w-4" style={{ color: '#C9A961' }} />
                  <span className="text-sm font-semibold" style={{ color: '#C9A961' }}>FM</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 pl-6">
              <div className="p-3 rounded-xl transition-all" style={{ backgroundColor: 'rgba(201, 169, 97, 0.15)', border: '1px solid rgba(201, 169, 97, 0.3)' }}>
                <ArrowUpRight className="h-6 w-6 group-hover:scale-110 transition-transform" style={{ color: '#C9A961' }} />
              </div>
              <span className="text-xs text-neutral-500 group-hover:text-[#C9A961] transition-colors">Enter</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Communication Hub with Pop-Out Chat */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Chat - With Pop-Out Option */}
        <Card className="bg-dark-card border-dark-border shadow-card hover:shadow-gold hover:border-gold/50 transition-all">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-4 rounded-xl bg-gradient-to-br from-[#C9A961]/20 to-transparent">
                <MessageSquare className="h-8 w-8 text-gold" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-1">Team Chat</h3>
                <p className="text-gray-400 text-sm mb-3">Slack-like messaging for your team</p>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-500">Live messaging</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => navigate('/chat')}
                    className="flex-1 bg-[#C9A961] text-black hover:bg-[#B8985A]"
                    size="sm"
                  >
                    Open Chat
                  </Button>
                  <Button 
                    onClick={() => window.open('/chat', 'TeamChat', 'width=800,height=900,left=100,top=100')}
                    variant="outline"
                    className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-black"
                    size="sm"
                    title="Open in new window"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Video Meetings */}
        <Card 
          className="bg-dark-card border-dark-border shadow-card hover:shadow-gold hover:border-gold/50 transition-all cursor-pointer"
          onClick={() => navigate('/meetings')}
        >
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-4 rounded-xl bg-gradient-to-br from-[#C9A961]/20 to-transparent">
                <Video className="h-8 w-8 text-gold" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-1">Video Meetings</h3>
                <p className="text-gray-400 text-sm mb-3">Host video conferences</p>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 bg-[#C9A961] rounded-full"></div>
                  <span className="text-xs text-gray-500">Powered by Jitsi</span>
                </div>
                <Button variant="outline" className="w-full border-gray-700 text-gray-400 hover:border-[#C9A961] hover:text-[#C9A961]" size="sm">
                  Start Meeting
                  <ArrowUpRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card className="bg-dark-card border-dark-border shadow-card">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-[#C9A961]" />
              Awaiting Your Action
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-gray-800">
                <span className="text-gray-400">Waiting On Me</span>
                <Badge className="bg-red-500/20 text-red-400">0</Badge>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-gray-800">
                <span className="text-gray-400">Pending Reviews</span>
                <Badge className="bg-orange-500/20 text-orange-400">0</Badge>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-black/40 border border-gray-800">
                <span className="text-gray-400">Due Today</span>
                <Badge className="bg-[#C9A961]/20 text-[#C9A961]">0</Badge>
              </div>
            </div>
            <Button 
              className="w-full mt-4 bg-transparent border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-black"
              size="sm"
              onClick={() => navigate('/command-center')}
            >
              View All Actions
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-dark-card border-dark-border shadow-card">
        <CardHeader className="border-b border-dark-border">
          <CardTitle className="text-gold">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { label: 'New Project', icon: FolderKanban, path: '/projects' },
              { label: 'Add Task', icon: CheckSquare, path: '/tasks' },
              { label: 'New Client', icon: Users, path: '/clients' },
              { label: 'Work Order', icon: Briefcase, path: '/work-orders' },
              { label: 'Estimates', icon: DollarSign, path: '/estimating' },
              { label: 'Schedule', icon: Calendar, path: '/schedules' },
            ].map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(action.path)}
                  className="p-4 rounded-xl bg-black/40 border border-dark-border hover:border-gold hover:bg-gold/5 transition-all duration-200 group"
                >
                  <Icon className="h-6 w-6 text-gold mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="text-sm text-gray-400 group-hover:text-gold transition-colors text-center">
                    {action.label}
                  </p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
